import {
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRowModel,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { DateTime } from 'luxon';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { useDeletePowerlistContactMutation } from '../../api/apigQuery';
import { LabelledOutline } from '../../common/final-form-material-ui';
import { MaterialAutocomplete } from '../../common/formik-material-ui';
import { reformatCamelCaseString } from '../../common/helperFunctions/formatter';
import { normalizePhoneSearch } from '../../common/helperFunctions/phoneSearchNormalizer';
import { Logger } from '../../common/Logger';
import {
  trackAddPowerListOnContacts,
  trackEditPowerListOnContacts,
  trackRefreshPowerListContacts,
} from '../../common/userTracking';
import { agentUserId } from '../../recoil/atoms/agentUserId';
import { IUserAtom, Role, user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { DeleteContactModal } from '../components/modals/DeleteContactModal';
import HistoryExportModal from '../components/modals/HistoryExportModal';
import { PowerlistModal } from '../components/modals/PowerlistModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { ActionColumn } from '../modules/Manage/pages/queues-table/ActionColumns';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  refreshDurations,
  ToolbarAddDeleteButton,
  ToolbarRefreshButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme: Theme) => ({
  dropdown: {
    marginLeft: '1vw',
    marginTop: '10px',
  },
  userStatusDropdown: {
    width: '150px',
    marginLeft: '1vw',
    // marginTop: '10px',
  },
  statsToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  powerlistStatsContainer: {
    height: 'fit-content',
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    '& [aria-label=outline-body-content]': {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      marginBottom: 0,
    },
  },
  powerlistStatsCard: {
    minWidth: '6rem',
    flex: '1 1 auto',
    margin: theme.spacing(0.25),
  },
  powerlistStatsCardContent: {
    padding: theme.spacing(1),
    '&:last-child': {
      paddingBottom: theme.spacing(1), // reset padding to default
    },
  },
  powerlistStatsCardTitle: {
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    fontWeight: 500,
  },
  powerlistStatsCardBody: {
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  summaryRefreshButtonContainer: {
    marginLeft: 'auto',
  },
  button: {
    marginRight: theme.spacing(1),
  },
}));

export function PowerlistContacts(props) {
  const history = useHistory();
  const { id: queryParamsId, powerlistId: queryParamsPowerlistId } = useParams<{
    id?: string;
    powerlistId?: string;
  }>();
  const classes = useStyles();
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { canManage, role } = permissionsLoadable.contents as IPermissions;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  // state for serverside pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [deleteItemId, setDeleteItemId] = useState<number | undefined>(
    undefined
  );
  const [selected, setSelected] = useState<GridRowModel[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'lastAttemptDateTime',
      sort: 'desc',
    },
  ]);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtom = userAtomLoadable.contents;
  const [autocomplete, setAutocomplete] = useState();
  const [showExportModal, setShowExportModal] = useState(false);
  const [csvProps, setCsvProps] = useState({});
  const profile = useLocation()?.pathname?.startsWith?.('/my-profile/');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChangeForAutocomplete = useCallback(
    (e, newValue) => {
      setAutocomplete(newValue);
      if (newValue !== null) {
        setUserAtom({
          ...userAtom,
          selectedPowerlistId: newValue ? newValue.id : '',
        });
        history.push(
          queryParamsPowerlistId
            ? profile
              ? `/my-profile/${
                  (userAtom as IUserAtom | undefined)?.userid
                }/powerlists/${newValue.id}/view-contacts`
              : `/manage/agents/${
                  (userAtom as IUserAtom | undefined)?.userid
                }/powerlists/${newValue.id}/view-contacts`
            : `/manage/powerlists/${newValue.id}/view-contacts`
        );
      }
    },
    [userAtom, history, queryParamsPowerlistId, profile, setUserAtom]
  );
  const agentUserIdAtom = useRecoilValueLoadable(agentUserId)?.contents;
  const [
    powerlistsReportCached,
    powerlistsReportLoadable,
    setRefetchForPowerlistsReport,
  ] = makeApiRequestCached(
    {
      endpoint: `${
        queryParamsPowerlistId
          ? endpointEnum.GET_USER_POWERLISTS
          : endpointEnum.GET_BUSINESS_POWERLISTS
      }`,
      method: 'POST',
      payloadJsonString: JSON.stringify(
        queryParamsPowerlistId
          ? {
              call: 'v1/getUsersPowerlists',
              userId:
                agentUserIdAtom && 'id' in agentUserIdAtom
                  ? agentUserIdAtom.id
                  : undefined,
              teamId:
                agentUserIdAtom && 'systemTeamId' in agentUserIdAtom
                  ? agentUserIdAtom.systemTeamId
                  : undefined,
            }
          : {
              call: 'v1/getBusinessPowerlists',
            }
      ),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data.powerlist ?? cached.current
  );
  powerlistsReportCached.current = useMemo(
    () =>
      powerlistsReportLoadable?.contents?.response?.data ??
      powerlistsReportCached?.current,
    [powerlistsReportLoadable, powerlistsReportCached]
  );

  const { treatment } = useFeatureFlag('SWARM-31-org-pl-privacy');
  const isOrgPlPrivacyEnabled = useMemo(() => {
    return treatment === 'control' || treatment === 'on';
  }, [treatment]);

  useEffect(() => {
    if (!isOrgPlPrivacyEnabled || !powerlistsReportCached?.current) {
      return;
    }

    const targetPowerlistId = Number(queryParamsPowerlistId ?? queryParamsId);
    const lists = powerlistsReportCached?.current?.powerlists;
    const current = lists?.find?.((l) => Number(l.id) === targetPowerlistId);

    const isOrg =
      current?.isAgentPowerlist === 0 || current?.type === 'Organizational';

    if (role === Role.USER && (!current || isOrg)) {
      enqueueSnackbarWithStyles({
        message:
          'Access denied: organizational PowerList contacts are restricted.',
        alertProps: { severity: 'error' },
      });
      const redirectPath = queryParamsPowerlistId
        ? profile
          ? `/my-profile/${queryParamsId}/powerlists`
          : `/manage/agents/${queryParamsId}/powerlists`
        : `/manage/powerlists`;
      history.replace(redirectPath);
    }
  }, [
    role,
    queryParamsId,
    queryParamsPowerlistId,
    profile,
    history,
    enqueueSnackbarWithStyles,
    powerlistsReportCached,
    isOrgPlPrivacyEnabled,
  ]);
  const [businessDataCached] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  Logger.debug({
    message:
      'businessDataCached-timezone_php=' +
      businessDataCached.current?.timezone_php,
  });
  const handlePageChange = (page: number) => {
    setPage(page);
  };
  const handlePageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
  };
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (
      newSortModel?.[0] &&
      (newSortModel[0].field !== sortModel[0].field ||
        newSortModel[0].sort !== sortModel[0].sort)
    ) {
      setSortModel(newSortModel);
    }
  };
  const mutation = useMutation(
    getApiMutation(userAtom as IUserAtom, setUserAtom)
  );
  const [deletePowerlistContactMutation] = useDeletePowerlistContactMutation();
  const rowsCached = useRef<{ id: number; status: string }[]>([]);
  /**
   *
   * NOTE: must use useMemo so rows do not get updated on re-render
   */

  const powerlistContactsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_POWERLIST_CONTACTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getPowerlistContacts',
        powerlistId: queryParamsPowerlistId ?? queryParamsId,
        startIndex: (page - 1) * pageSize,
        size: pageSize,
        search: normalizePhoneSearch(search)?.[0] || search,
        contactStatus: status,
        field: sortModel?.[0]?.field,
        sort: sortModel?.[0]?.sort,
      }),
    })
  );

  /* Will not be using this for now and instead will refresh the page
   * using powerlistContactsLoadable
  const powerlistContactsSummaryLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_POWERLIST_CONTACTS_SUMMARY}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getPowerlistContactsSummary',
        powerlistId: queryParamsPowerlistId ?? queryParamsId,
        startIndex: (page - 1) * pageSize,
        size: pageSize,
        search,
        contactStatus: status,
        field: sortModel?.[0]?.field,
        sort: sortModel?.[0]?.sort,
      }),
    })
  );
  *
  */

  const setRefetchToggleForPowerlistContacts = useSetRecoilState(
    refetchToggle(`${endpointEnum.GET_POWERLIST_CONTACTS}`)
  );
  /* Will not be using this for now and instead will refresh the page
   * using powerlistContactsLoadable
  const setRefetchToggleForPowerlistContactsSummary = useSetRecoilState(
    refetchToggle(`${endpointEnum.GET_POWERLIST_CONTACTS_SUMMARY}`)
  );
  */
  const total =
    Number(
      powerlistContactsLoadable?.contents?.response?.data?.result?.total
    ) || 0;
  const totalInPowerlist =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats?.total ??
    0;
  const countQueued =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.countQueued ?? '-';
  const countInProgress =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.countInProgress ?? '-';
  const countDeferred =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.countDeferred ?? '-';
  const countRemoved =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.countRemoved ?? '-';

  const statDialed =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.statDialed ?? '-';
  const statAnswered =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.statAnswered ?? '-';
  const statConnected =
    powerlistContactsLoadable?.contents?.response?.data?.result?.stats
      ?.statConnected ?? '-';
  const statTalkTime = powerlistContactsLoadable?.contents?.response?.data
    ?.result?.stats?.statTalkTimeInSec
    ? new Date(
        powerlistContactsLoadable.contents.response.data.result.stats
          .statTalkTimeInSec * 1000
      )
        .toISOString()
        .substr(11, 8)
    : '-';

  const reduceForRows = (obj) => {
    const row = { ...obj, id: obj.powerlistContactId };

    if (obj.status === 'enqueue') {
      row.status = 'queued';
    } else if (obj.status === 'dequeue') {
      row.status = 'removed';
    }
    return row;
  };

  rowsCached.current = useMemo(
    () =>
      powerlistContactsLoadable.state === 'hasValue'
        ? (
            powerlistContactsLoadable?.contents?.response?.data?.result
              ?.powerlistContacts ?? []
          )
            ?.map?.((ca) => reduceForRows(ca))
            ?.sort?.((a, b) => {
              if (sortModel?.[0]?.sort === 'asc') {
                return a[sortModel?.[0]?.field] > b[sortModel?.[0]?.field]
                  ? 1
                  : -1;
              } else {
                return b[sortModel?.[0]?.field] > a[sortModel?.[0]?.field]
                  ? 1
                  : -1;
              }
            })
        : [],
    [powerlistContactsLoadable, sortModel]
  );

  const handleDeletion = async (id) => {
    let message = 'Update sucessful'; // ! to ignore usage before assignment error
    let severity: AlertColor = 'success';

    const cannotDelete = rowsCached.current.find(
      (el) =>
        Number(el.id) === Number(id) &&
        el.status === 'answeredWaitingForOutcome'
    );
    if (cannotDelete) {
      return enqueueSnackbarWithStyles({
        message: 'Contact(s) waiting for outcome cannot be deleted',
        alertProps: {
          severity: 'error',
        },
      });
    }
    try {
      if (!(userAtom && 'businessid' in userAtom)) {
        throw new Error('Expected userAtom with businessid');
      }
      if (!deleteItemId) {
        throw new Error('Expected deleteItemId to be defined');
      }

      const response = await deletePowerlistContactMutation({
        userSettings: {
          apikey: userAtom.apikey,
          businessId: userAtom.businessid,
          userid: userAtom.userid,
        },
        powerlistContactId: deleteItemId,
      });
      if (
        !response ||
        'error' in response ||
        ('data' in response && !response.data.success)
      ) {
        throw new Error(
          'deletePowerlistContactMutation failed: ' +
            JSON.stringify(
              (response as
                | { error: FetchBaseQueryError | SerializedError }
                | undefined)?.error
            ) +
            ' ' +
            JSON.stringify(
              (response as { data: { success: boolean } } | undefined)?.data
            )
        );
      }

      // log success
      Logger.trace(
        {
          message,
          response,
        },
        'deletePowerlistContact'
      );

      // refetch business queues
      setRefetchToggleForPowerlistContacts({});
    } catch (exception: unknown) {
      message = 'Failed to delete powerlist contact (please try again later)';
      severity = 'error';
      const err = exception as {
        name?: string;
        message?: string;
        stack?: string;
      };
      Logger.error(
        {
          message,
          exception: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
          },
        },
        'deletePowerlistContact'
      );
    }

    // clear selections list
    setDeleteItemId(undefined);
    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity: severity,
      },
    });
  };

  function apostropheFormatter(params) {
    return params.value.replace(/â\?\?/g, "'");
  }
  const columns = [
    {
      field: 'powerlistId',
      headerName: 'PowerList ID',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: true,
    },
    {
      field: 'powerlistContactId',
      headerName: 'Contact ID',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: true,
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.value ?? '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params: GridValueGetterParams) =>
        params.value == 'queued' &&
        DateTime.fromISO(params.row.retryAfterDateTime, { zone: 'UTC' }) >
          DateTime.utc()
          ? 'Deferred'
          : reformatCamelCaseString(params.value as string),
    },
    {
      field: 'lastDialOutcome',
      headerName: 'Last Dial Outcome',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        params.row.lastDialOutcome === 'Answered - not disposed'
          ? 'Answered - No Call Outcome'
          : params.row.lastDialOutcome ?? '-',
    },
    {
      field: 'deferredReason',
      headerName: 'Deferred Reason',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.deferredReason ?? '-',
    },
    {
      field: 'title',
      headerName: 'Title',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'contactName',
      headerName: 'Contact Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: apostropheFormatter,
    },
    {
      field: 'email',
      headerName: 'Email',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'reservedFor',
      headerName: 'Reserved For',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: true,
      valueFormatter: (params) => params.row.reservedForUserName ?? '-',
    },
    {
      field: 'targetTimeZone',
      headerName: 'Target Time Zone',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.targetTimeZone ?? '-',
    },
    {
      field: 'attemptCount',
      headerName: 'Attempt Count',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.attemptCount ?? '-',
    },
    {
      field: 'lastAttemptDateTime',
      headerName: 'Last Attempted Date',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        params.row.lastAttemptDateTime
          ? DateTime.fromISO(params.row.lastAttemptDateTime, {
              zone: businessDataCached.current?.timezone_php,
            }).toLocaleString(DateTime.DATETIME_SHORT)
          : '-',
    },
    {
      field: 'retryAfterDateTime',
      headerName: 'Retry After Date',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        params.row.retryAfterDateTime
          ? DateTime.fromISO(params.row.retryAfterDateTime, {
              zone: businessDataCached.current?.timezone_php,
            }).toLocaleString(DateTime.DATETIME_SHORT)
          : '-',
    },
    {
      field: 'createdDate',
      headerName: 'Date Added',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        params.row.createdDate
          ? DateTime.fromISO(params.row.createdDate, {
              zone: businessDataCached.current?.timezone_php,
            }).toLocaleString(DateTime.DATETIME_SHORT)
          : '-',
    },
    {
      field: 'statConnected',
      headerName: 'Connected',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.statConnected ?? '-',
    },
    {
      field: 'statAnswered',
      headerName: 'Answered',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.statAnswered ?? '-',
    },
    {
      field: 'owner',
      headerName: 'Owner',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: true,
      valueFormatter: (params) => params.row.ownerUserName ?? '-',
    },
    {
      field: 'spreadsheet',
      headerName: 'Spreadsheet',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: true,
      valueFormatter: (params) => params.row.powerlistSpreadsheetName ?? '-',
    },
    {
      field: 'rowNumber',
      headerName: 'Spreadsheet Row #',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: true,
    },
    {
      field: 'ssData',
      headerName: 'ssData',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
      disableExport: false,
      valueFormatter: (params) => JSON.stringify(params.row.ssData),
    },
    {
      field: '_actions',
      headerName: '\xa0\xa0\xa0\xa0\xa0\xa0\xa0' + 'Actions',
      width: 100,
      enableSorting: false,
      disableExport: true,
      disableSearch: true, // do not search field content
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      hide: canManage === false, // must be explicit compare else warning: React has detected a change in the order of Hooks called by null
      renderCell: (params) => {
        const AC = ActionColumn({
          deleteItemId,
          noFiller: true,
          setDeleteItemId: setDeleteItemId as Dispatch<
            SetStateAction<string | number | undefined>
          >,
          isLoading: mutation.isLoading,
          handleDeletion: handleDeletion,
          showDelete: params.row.status !== 'removed',
        });
        return AC(params as GridValueGetterParams);
      },
    },
  ] as IColDef[];
  const handleSearchChange = useCallback(
    (value) => {
      // reset page back to 1
      setPage(1);
      setSearch(value);
    },
    [setSearch]
  );
  const [deleteModal, handleDeleteModal] = useState(false);
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Powerlist-Contact"
        numSelected={selected.length}
        search={search}
        setSearch={handleSearchChange}
        debounceTimeout={1000} // add 1/2 second debounce vs 100ms default value
        isLoading={powerlistContactsLoadable.state === 'loading'} // do not block on search
        leftComponent={
          <>
            <FormControl
              className={classes.userStatusDropdown}
              size="small"
              variant="outlined"
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as string)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="enqueue">Queued</MenuItem>
                <MenuItem value="inProgress">In Progress</MenuItem>
                <MenuItem value="deferred">Deferred</MenuItem>
                <MenuItem value="removed">Removed</MenuItem>
                <MenuItem value="dequeueForwardToCadence">
                  Forward To Cadence
                </MenuItem>
                <MenuItem value="dequeueForwardToPowerlist">
                  Forward To PowerList
                </MenuItem>
              </Select>
            </FormControl>
            <MaterialAutocomplete
              className={`flex-grow-1`}
              style={{
                width: '300px',
                paddingLeft: '5px',
                paddingRight: '5px',
              }}
              type="text"
              label="PowerLists"
              size="small"
              variant="outlined"
              options={
                powerlistsReportCached.current?.powerlists?.length
                  ? powerlistsReportCached.current?.powerlists.map((list) => ({
                      label: list.name,
                      id: list.powerlistId,
                    }))
                  : []
              }
              value={
                autocomplete ?? {
                  label: powerlistsReportCached.current?.powerlists?.filter(
                    (list) =>
                      list.powerlistId ===
                      Number(queryParamsPowerlistId ?? queryParamsId ?? 0)
                  )?.[0]?.name,
                }
              }
              multiple={false}
              onChange={handleChangeForAutocomplete}
              getOptionLabel={(option) => {
                return option.label ?? '';
              }}
              isOptionEqualToValue={(option, value) => {
                return option?.label?.powerlistId === value?.label?.powerlistId;
              }}
              disabled={false}
            />
          </>
        }
        rightComponent={
          <div
            style={{
              height: '39px',
              marginLeft: 'auto',
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
            }}
          >
            {role === Role.SUPERADMIN && (
              <>
                <Button
                  className={classes.button + ' superStyle'}
                  color="primary"
                  variant="contained"
                  disableElevation={true}
                  disabled={props.isLoading}
                  onClick={() => {
                    trackEditPowerListOnContacts();
                    history.push(
                      queryParamsPowerlistId
                        ? profile
                          ? `/my-profile/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/edit`
                          : `/manage/agents/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/edit`
                        : `/manage/powerlists/${queryParamsId}/view-contacts/edit`
                    );
                  }}
                >
                  Edit PowerList
                </Button>
              </>
            )}
            <ToolbarAddDeleteButton
              addButtonText="Add"
              numSelected={selected.length}
              onClickForAdd={() => {
                trackAddPowerListOnContacts();
                history.push(
                  queryParamsPowerlistId
                    ? profile
                      ? `/my-profile/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/add`
                      : `/manage/agents/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/add`
                    : `/manage/powerlists/${queryParamsId}/view-contacts/add`
                );
              }}
              onClickForDelete={handleDeleteModal as () => void}
              isLoading={props.isLoading}
            />
          </div>
        }
        csvExport={true}
        onCsvExport={(dataType, start, end, csvData) => {
          setCsvProps({
            dataType: dataType,
            start: start,
            end: end,
            csvData: csvData,
          });
          setShowExportModal(true);
        }}
      />
    ),
    [
      search,
      powerlistContactsLoadable,
      status,
      handleDeleteModal,
      selected.length,
      classes,
      role,
      queryParamsId,
      queryParamsPowerlistId,
      profile,
      autocomplete,
      handleChangeForAutocomplete,
      handleSearchChange,
      history,
      powerlistsReportCached,
    ]
  );

  return (
    <>
      <div
        aria-label="statsToolbar"
        className={'testexp5000 ' + classes.statsToolbar}
      >
        <LabelledOutline
          className={classes.powerlistStatsContainer}
          label={`Summary Count`}
          customPadding="8px"
          backgroundColor="#f7f5f5"
        >
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Total number of contacts in this Powerlist ready to be dialed now or scheduled to be dialed at a later time">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Total
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {totalInPowerlist}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Total number of contacts in this Powerlist ready to be dialed">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Queued
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {countQueued}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Total number of contacts in this Powerlist that are Dialing, Reserved or Answered Waiting for Outcome">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  In Progress
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {countInProgress}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Total number of contacts in this Powerlist scheduled to be dialed at a later time">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Deferred
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {countDeferred}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Total number of contacts removed from this Powerlist and will not be dialed">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Removed
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {countRemoved}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
        </LabelledOutline>
        <LabelledOutline
          className={classes.powerlistStatsContainer}
          label={`PowerDialer Stats`}
          customPadding="8px"
          backgroundColor="#f7f5f5"
        >
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Number of contacts Dialed from this powerlist by all agents across their PowerDialer sessions">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Dialed
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {statDialed}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Number of contacts answered by all agents across all PowerDialer sessions">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Answered
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {statAnswered}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Number of contacts connected by all agents across all PowerDialer sessions">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Connected
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {statConnected}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
          <Card className={classes.powerlistStatsCard}>
            <Tooltip title="Total talk time by all agents across all PowerDialer sessions">
              <CardContent className={classes.powerlistStatsCardContent}>
                <Typography
                  color="textSecondary"
                  className={classes.powerlistStatsCardTitle}
                >
                  Talk Time
                </Typography>
                <Typography className={classes.powerlistStatsCardBody}>
                  {statTalkTime}
                </Typography>
              </CardContent>
            </Tooltip>
          </Card>
        </LabelledOutline>
        <div className={classes.summaryRefreshButtonContainer}>
          <ToolbarRefreshButton
            id="PowerlistContacts"
            duration={refreshDurations.dur30sec}
            onClickForRefresh={() => {
              trackRefreshPowerListContacts();
              setRefetchToggleForPowerlistContacts({});
            }}
            isLoading={props.isLoading}
          />
        </div>
      </div>
      <Paper square={true} elevation={0}>
        <DataTable
          setSelected={setSelected}
          rows={rowsCached?.current ?? []}
          columns={columns}
          // TO DO: return total from server
          rowCount={total}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          loading={powerlistContactsLoadable.state === 'loading'}
          ToolbarComponent={ToolbarComponent}
          pagination
          enableSelectAll={true}
          selected={selected}
        />
        <Switch>
          <Route
            path={
              queryParamsPowerlistId
                ? profile
                  ? `/my-profile/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/edit`
                  : `/manage/agents/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/edit`
                : `/manage/powerlists/${queryParamsId}/view-contacts/edit`
            }
          >
            {({ history, match }) => (
              <PowerlistModal
                agent={queryParamsPowerlistId ? queryParamsId : undefined}
                id={queryParamsPowerlistId ?? queryParamsId ?? null}
                show={match != null}
                defaultTabIdx={0}
                handleClose={() => {
                  history.push(
                    queryParamsPowerlistId
                      ? profile
                        ? `/my-profile/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts`
                        : `/manage/agents/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts`
                      : `/manage/powerlists/${queryParamsId}/view-contacts`
                  );
                  setRefetchForPowerlistsReport({
                    endpoint: `${
                      queryParamsPowerlistId
                        ? endpointEnum.GET_USER_POWERLISTS
                        : endpointEnum.GET_BUSINESS_POWERLISTS
                    }`,
                    method: 'POST',
                    payloadJsonString: JSON.stringify(
                      queryParamsPowerlistId
                        ? {
                            call: 'v1/getUsersPowerlists',
                            userId:
                              agentUserIdAtom && 'id' in agentUserIdAtom
                                ? agentUserIdAtom.id
                                : undefined,
                            teamId:
                              agentUserIdAtom &&
                              'systemTeamId' in agentUserIdAtom
                                ? agentUserIdAtom.systemTeamId
                                : undefined,
                          }
                        : {
                            call: 'v1/getBusinessPowerlists',
                          }
                    ),
                  });
                }}
                enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
              />
            )}
          </Route>
          <Route
            path={
              queryParamsPowerlistId
                ? profile
                  ? `/my-profile/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/add`
                  : `/manage/agents/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts/add`
                : `/manage/powerlists/${queryParamsId}/view-contacts/add`
            }
          >
            {({ history, match }) => (
              <PowerlistModal
                agent={queryParamsPowerlistId ? queryParamsId : undefined}
                id={null}
                show={match != null}
                defaultTabIdx={2}
                handleClose={() => {
                  history.push(
                    queryParamsPowerlistId
                      ? profile
                        ? `/my-profile/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts`
                        : `/manage/agents/${queryParamsId}/powerlists/${queryParamsPowerlistId}/view-contacts`
                      : `/manage/powerlists/${queryParamsId}/view-contacts`
                  );
                  setRefetchForPowerlistsReport({
                    endpoint: `${
                      queryParamsPowerlistId
                        ? endpointEnum.GET_USER_POWERLISTS
                        : endpointEnum.GET_BUSINESS_POWERLISTS
                    }`,
                    method: 'POST',
                    payloadJsonString: JSON.stringify(
                      queryParamsPowerlistId
                        ? {
                            call: 'v1/getUsersPowerlists',
                            userId:
                              agentUserIdAtom && 'id' in agentUserIdAtom
                                ? agentUserIdAtom.id
                                : undefined,
                            teamId:
                              agentUserIdAtom &&
                              'systemTeamId' in agentUserIdAtom
                                ? agentUserIdAtom.systemTeamId
                                : undefined,
                          }
                        : {
                            call: 'v1/getBusinessPowerlists',
                          }
                    ),
                  });
                }}
                enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
              />
            )}
          </Route>
        </Switch>
        <DeleteContactModal
          setRefetchToggleForPowerlistContacts={
            setRefetchToggleForPowerlistContacts
          }
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          selected={selected}
          show={!!deleteModal}
          userAtom={userAtom as IUserAtom}
          deletePowerlistContactMutation={deletePowerlistContactMutation}
          handleClose={() => handleDeleteModal(false)}
        />
        <HistoryExportModal
          show={showExportModal}
          handleClose={() => {
            setShowExportModal(false);
          }}
          page={page}
          pageSize={pageSize}
          totalRows={total}
          type={'powerlist-contacts'}
          csvProps={csvProps}
          rows={rowsCached?.current ?? []}
          powerlistId={
            !isNaN(Number(queryParamsPowerlistId ?? queryParamsId))
              ? Number(queryParamsPowerlistId ?? queryParamsId)
              : undefined
          }
          filters={{
            search: search,
            sortModel: sortModel,
            status: status,
          }}
        />
      </Paper>
    </>
  );
}
